import { defineComponent, h, nextTick } from 'vue';
import isEqual from 'lodash/isEqual';
import utils from '../../../Shared/utils.jsx';
import methods from '../../../Shared/methods';
import AceEditor from '../3rdParty/AceEditor.jsx';

export default defineComponent({
    name: 'form-text-editor',
    components: {
        AceEditor,
    },
    data: function () {
        return {
            israw: false,
            visible_keys: {},
            type: 'FormField',
            height: null,
        }
    },
    created() {
        if (this.schema && this.schema.formatData)
            this.height = this.schema.formatData.Height;
    },
    mounted() {
        nextTick(() => this.ensureEditorResizeHostObserver());
    },
    updated() {
        nextTick(() => this.ensureEditorResizeHostObserver());
    },
    beforeUnmount() {
        this.teardownEditorResizeHostObserver();
    },
    computed: {
        ...utils.forms.computed,
    },
    methods: {
        ...methods,
        rememberResizedHeight() {
            const host = this.$refs.editorResizeHost;
            if (!host || !this.schema)
                return;

            const resizedHeight = Math.round(host.offsetHeight || 0);
            if (!resizedHeight)
                return;

            if (this.height !== resizedHeight)
                this.height = resizedHeight;
        },
        ensureEditorResizeHostObserver() {
            if (typeof ResizeObserver === 'undefined')
                return;
            const host = this.$refs.editorResizeHost;
            if (!host) {
                this.teardownEditorResizeHostObserver();
                return;
            }
            if (this._editorResizeHostObserved === host)
                return;
            this.teardownEditorResizeHostObserver();
            this._editorResizeHostObserved = host;
            this._editorResizeHostObserver = new ResizeObserver(() => this.rememberResizedHeight());
            this._editorResizeHostObserver.observe(host);
        },
        teardownEditorResizeHostObserver() {
            if (this._editorResizeHostObserver) {
                this._editorResizeHostObserver.disconnect();
                this._editorResizeHostObserver = null;
            }
            this._editorResizeHostObserved = null;
        },
        sync(prop, value) {
            if (this.israw) {
                if (this.cmodel[prop] === value)
                    return;
                this.cmodel[prop] = value;
            }
            else {
                let obj;
                try {
                    obj = JSON.parse(value);
                }
                catch (e) {
                    utils.warn('Input text editor failed to convert value to object: ' + e);
                    return;
                }

                const prev = this.cmodel[prop];
                if (typeof prev === 'object' && prev !== null && isEqual(prev, obj))
                    return;

                this.cmodel[prop] = obj;
            }
        },
        populateDefaultTemplate(e, template) {
            this.cmodel[this.name] = template;
        },
    },
    props: {
        name: '',
        schemakey: '',
        root: null,
        schema: null,
        cmodel: null,
        extra: null,
        readonly: false,
        path: null,
        child: 0,
        uniquerowkey: null,
        propertygrid: false,
    },
    render() {
        if (!this.Condition)
            return null;

        let desc;
        if (this.schema.description)
            desc = <i class="mdi mdi-information" title={this.schema.description} style={{ color: "var(--v-checkboxGrey-base)", fontSize: "small", marginLeft: "3px" }}></i>;

        const name = <span style={{ marginLeft: (this.child * 15) + "px" }}>{this.name}</span>;

        const visible_key = [...this.path, this.schemakey].join('.');
        const vis = utils.getVisibility(this, visible_key, 'false');
        const icon = vis ? <i class="mdi mdi-menu-down"></i> : <i class="mdi mdi-menu-right"></i>;
        const openclose = <span style={{ fontSize: "large", cursor: "pointer" }} onClick={() => utils.toggleVisible(this, visible_key, 'false')}>{icon}</span>;

        let value;
        let lang;
        if (this.schema.formatData) {
            this.israw = this.schema.formatData.IsRaw;
            lang = this.schema.formatData.Language || 'javascript';
        }
        else {
            this.israw = false;
            lang = 'json';
        }

        if (this.israw)
            value = this.cmodel[this.schemakey] || '';
        else
            value = this.cmodel[this.schemakey] ? JSON.stringify(this.cmodel[this.schemakey], null, 4) : '';

        let input;
        let link;
        let height = '100px';
        if (this.height != null && this.height > 0)
            height = this.height + 'px';

        let controls;
        if (this.schema.formatData && this.schema.formatData.Controls && this.schema.formatData.Controls.length > 0) {
            controls = [];
            for (let i = 0; i < this.schema.formatData.Controls.length; i++) {
                const c = this.schema.formatData.Controls[i];

                let DynamicControl = utils.getDynamicComponent(h, c);
                if (!DynamicControl)
                    DynamicControl = 'default-unknown';

                if (!c.$objectId) c.$objectId = utils.generateUUID();

                controls.push(
                    <DynamicControl
                        key={c.$objectId}
                        type={c.ControlType}
                        name={c.ControlData ? c.ControlData.Name : ''}
                        root={this.root}
                        designmodel={c}
                        parentType="HorizontalLayout"
                        controlData={c.ControlData}
                        controlName={c.Name}
                        accessPermission={c.AccessPermission}
                    >
                    </DynamicControl>
                );
            }
        }

        if (vis) {
            if (this.readonly)
                input = (
                    <td class={{ "property-grid": this.propertygrid }} style={{ width: "100%", height: "100px", ...(this.propertygrid ? { minWidth: 0 } : {}) }}>
                        <textarea cols="40" disabled="disabled">{value}</textarea>
                    </td>
                );
            else {
                if (controls)
                    controls = <div style="display: flex;">{controls}</div>;

                input = (
                    <td class={{ "property-grid": this.propertygrid }} style={{ width: "100%", height: height, ...(this.propertygrid ? { minWidth: 0 } : {}) }}>
                        {controls}
                        <div ref="editorResizeHost" style={{ resize: "vertical", width: "97%", height: height, borderStyle: "solid", borderColor: "silver", borderWidth: "1px", padding: "5px", overflow: "auto", position: "relative" }}>
                            <AceEditor
                                editorId={'Editor_' + this.uniquerowkey}
                                lang={lang}
                                onChangeContent={(text) => this.sync(this.schemakey, text)}
                                content={value || ''}>
                            </AceEditor>
                        </div>
                    </td>
                );
                if (this.schema.formatData && this.schema.formatData.DefaultTemplate)
                    link = (
                        <span style={{ cursor: "pointer" }} onClick={(e) => this.populateDefaultTemplate(e, this.schema.formatData.DefaultTemplate)}>
                            <i class="mdi mdi-chevron-right-box-outline" title="Set to default template" style={{ color: "lightblue", fontSize: "small", marginLeft: "2px" }}></i>
                        </span>
                    );
            }
        }
        else
            input = (
                <td class={{ "property-grid": this.propertygrid }} style={{ width: "100%", borderStyle: "solid", borderColor: "silver", borderWidth: "1px", ...(this.propertygrid ? { minWidth: 0 } : {}) }}>
                    <span>{value.length} byte(s)</span>
                </td>
            );

        return (
            <tr>
                <td style={{ width: "1px", verticalAlign: "top", whiteSpace: "nowrap" }}>
                    {name} {openclose} {desc} {link}
                </td>
                {input}
            </tr>
        );
    }
});